import { useNavigate, useParams } from '@tanstack/react-router';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import { Button, toast } from '@devhop/ui';
import type { UpdateCompanyPolicyDTO } from '@/modules/hr/company-policy/domain/types';
import {
    useCompanyPolicyDetailQuery,
    useUpdateCompanyPolicyMutation,
} from '../api/queries';
import { PolicyEditTour, usePolicyEditTour } from '../tour/PolicyEditTour';
import { PolicyForm } from '../ui/PolicyForm';
import { ArrowLeft, HelpCircle } from 'lucide-react';

export function PolicyEditPage() {
    const { id } = useParams({ strict: false });
    const nav = useNavigate();
    const detailQuery = useCompanyPolicyDetailQuery(id);
    const updateMutation = useUpdateCompanyPolicyMutation();
    const policy = detailQuery.data;
    const tour = usePolicyEditTour();

    const handleSubmit = (values: UpdateCompanyPolicyDTO) => {
        if (!id) return;
        updateMutation.mutate(
            { id, dto: values },
            {
                onSuccess: () => {
                    toast.success('ບັນທຶກການປ່ຽນແປງສຳເລັດ');
                    nav({ to: '/app/hr/company-policy/$id', params: { id } });
                },
                onError: () => {
                    toast.error('ບໍ່ສາມາດບັນທຶກໄດ້');
                },
            },
        );
    };

    if (detailQuery.isLoading || !policy) {
        return (
            <>
                <Header />
                <Main>
                    <div className="text-muted-foreground">
                        {detailQuery.isLoading
                            ? 'ກຳລັງໂຫຼດ...'
                            : 'ບໍ່ພົບນະໂຍບາຍ'}
                    </div>
                </Main>
            </>
        );
    }

    const handleBack = () => {
        nav({
            to: '/app/hr/company-policy',
            search: {
                offset: 0,
                limit: 20,
                policyTypeId: undefined,
                status: undefined,
                search: undefined,
            },
        });
    };

    return (
        <>
            <Header />
            <Main>
                <PolicyEditTour
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center justify-between"
                    data-tourid="policy-edit-back"
                >
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className="font-semibold text-2xl">
                                ແກ້ໄຂນະໂຍບາຍ
                            </h1>
                            <Button
                                variant="ghost"
                                size="icon"
                                className="size-7 rounded-full"
                                onClick={tour.startTour}
                                title="ເລີ່ມທົດສອບການນຳທາງ"
                                aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                            >
                                <HelpCircle className="size-5" />
                            </Button>
                        </div>
                        <p className="text-muted-foreground text-sm">
                            {policy.title}
                        </p>
                    </div>
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBack}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບໄປລາຍການ
                    </Button>
                </div>

                <div
                    className="mx-auto max-w-3xl"
                    data-tourid="policy-edit-form"
                >
                    <PolicyForm
                        mode="edit"
                        policy={policy}
                        onSubmit={handleSubmit}
                        onCancel={() =>
                            nav({
                                to: '/app/hr/company-policy/$id',
                                params: { id: id ?? '' },
                            })
                        }
                        isSubmitting={updateMutation.isPending}
                    />
                </div>
            </Main>
        </>
    );
}
